import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  useTheme,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
} from '@mui/material';
import {
  Fingerprint as FingerprintIcon,
  Delete as DeleteIcon,
  PhoneAndroid as PhoneIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { startRegistration } from '@simplewebauthn/browser';
import api from 'state/axios';

const WEBAUTHN_SUPPORTED = typeof window !== 'undefined' && !!window.PublicKeyCredential;

const BiometricSetup = () => {
  const theme = useTheme();

  const [passkeys, setPasskeys]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [registering, setRegistering] = useState(false);
  const [deviceName, setDeviceName]   = useState('');

  const loadPasskeys = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/crewmembers/webauthn/passkeys');
      setPasskeys(data.passkeys || []);
    } catch (err) {
      console.error('Failed to load passkeys:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (WEBAUTHN_SUPPORTED) loadPasskeys();
  }, [loadPasskeys]);

  const handleRegister = useCallback(async () => {
    try {
      setRegistering(true);
      const { data: options } = await api.get('/crewmembers/webauthn/registration-options');
      const response = await startRegistration({ optionsJSON: options });
      await api.post('/crewmembers/webauthn/registration-verify', { response, deviceName });
      toast.success('Passkey registered successfully!');
      setDeviceName('');
      await loadPasskeys();
    } catch (err) {
      if (err.name === 'InvalidStateError') {
        toast.error('This authenticator is already registered.');
      } else if (err.name === 'NotAllowedError') {
        toast.error('Registration cancelled.');
      } else {
        toast.error(err.response?.data?.message || err.message || 'Registration failed.');
      }
    } finally {
      setRegistering(false);
    }
  }, [deviceName, loadPasskeys]);

  const handleDelete = useCallback(async (passkeyId) => {
    try {
      await api.delete(`/crewmembers/webauthn/passkeys/${passkeyId}`);
      setPasskeys((prev) => prev.filter((pk) => pk._id !== passkeyId));
      toast.success('Passkey removed.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove passkey.');
    }
  }, []);

  if (!WEBAUTHN_SUPPORTED) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Your browser does not support biometric authentication (WebAuthn).
      </Alert>
    );
  }

  return (
    <Box
      component="fieldset"
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography component="legend" variant="subtitle1">
        Biometric Login (Passkeys)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Register fingerprint or Face ID for faster, passwordless login.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : passkeys.length > 0 ? (
        <List dense disablePadding>
          {passkeys.map((pk) => (
            <ListItem
              key={pk._id}
              disableGutters
              sx={{ py: 1 }}
              secondaryAction={
                <Box display="flex" alignItems="center" gap={1}>
                  {pk.credentialBackedUp && (
                    <Chip variant="outlined" size="small" label="Synced" color="info" />
                  )}
                  <Tooltip title="Remove passkey">
                    <IconButton size="small" color="error" onClick={() => handleDelete(pk._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            >
              {pk.credentialDeviceType === 'multiDevice'
                ? <PhoneIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                : <ComputerIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
              }
              <ListItemText
                primary={pk.deviceName || 'Unnamed device'}
                secondary={`Registered ${new Date(pk.registeredAt).toLocaleDateString()}`}
                slotProps={{
                  primary:   { variant: 'body2', fontWeight: 500 },
                  secondary: { variant: 'caption' },
                }}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.disabled" mb={1}>
          No passkeys registered yet.
        </Typography>
      )}

      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          fullWidth
          size="small"
          label="Device name (optional)"
          value={deviceName}
          placeholder="e.g. MacBook, iPhone"
          onChange={(e) => setDeviceName(e.target.value)}
        />
        <Button
          fullWidth
          variant="contained"
          onClick={handleRegister}
          disabled={registering}
          startIcon={registering ? <CircularProgress size={16} /> : <FingerprintIcon />}
        >
          {registering ? 'Registering…' : 'Register This Device'}
        </Button>
      </Box>
    </Box>
  );
};

export default BiometricSetup;
